export type FaceEnrollmentPose = 'front' | 'left' | 'right';

export const ENROLLMENT_POSES: readonly FaceEnrollmentPose[] = [
  'front',
  'left',
  'right',
];

export const ENROLLMENT_POSE_SHORT_LABELS: Record<FaceEnrollmentPose, string> = {
  front: 'Front',
  left: 'Left',
  right: 'Right',
};

export const ENROLLMENT_POSE_LABELS: Record<FaceEnrollmentPose, string> = {
  front: 'Look straight at the camera',
  left: 'Turn your head to the left',
  right: 'Turn your head to the right',
};

export const ENROLLMENT_POSE_HINTS: Record<FaceEnrollmentPose, string> = {
  front: 'Center your face and look directly at the camera.',
  left: 'Turn your head to your left so your right cheek faces the camera.',
  right: 'Turn your head to your right so your left cheek faces the camera.',
};

export const ENROLLMENT_POSE_READY_MESSAGES: Record<FaceEnrollmentPose, string> = {
  front: 'Looking straight — capture now',
  left: 'Left pose matched — capture now',
  right: 'Right pose matched — capture now',
};

/** Face looking at the camera. ML Kit yaw is ~0. */
const FRONT_YAW_MAX = 12;
/**
 * Minimum |yaw| for a left/right turn.
 * Gap vs FRONT_YAW_MAX rejects the same straight pose on later captures.
 */
const TURN_YAW_MIN = 18;
/** Ignore extreme profiles that are too far for enrollment quality. */
const TURN_YAW_MAX = 50;

/**
 * Classify head yaw for a front camera.
 * ML Kit: positive yaw = face looking toward the camera's right = user's left.
 */
export function classifyFaceYaw(yawAngle: number): FaceEnrollmentPose | 'unknown' {
  if (!Number.isFinite(yawAngle)) return 'unknown';
  if (Math.abs(yawAngle) <= FRONT_YAW_MAX) return 'front';
  if (yawAngle >= TURN_YAW_MIN && yawAngle <= TURN_YAW_MAX) return 'left';
  if (yawAngle <= -TURN_YAW_MIN && yawAngle >= -TURN_YAW_MAX) return 'right';
  return 'unknown';
}

export function requiredPoseForSampleIndex(index: number): FaceEnrollmentPose {
  const pose = ENROLLMENT_POSES[index];
  return pose ?? 'front';
}

export function poseGuidanceMessage(
  required: FaceEnrollmentPose,
  detected: FaceEnrollmentPose | 'unknown' | null,
): string {
  if (detected === required) return ENROLLMENT_POSE_READY_MESSAGES[required];
  if (detected == null) return 'Position your face in the frame.';
  return ENROLLMENT_POSE_HINTS[required];
}
